// The time zone an installed till runs on. Pure logic, kept out of the
// component so the one rule that matters — a till never leaves the bench on a
// zone nobody chose — is testable without rendering anything.
//
// 🔴 WHY THIS IS MANDATORY RATHER THAN A DEFAULT.
//
// An Android till ships on the factory zone and nothing in provisioning ever
// changed it. A Foggy Frog A10 ran live in production on `Asia/Shanghai`
// against an `America/New_York` store — twelve hours out, discovered only by
// holding it next to a browser till and reading the two clocks.
//
// The damage is not the clock face. Shift open/close boundaries, clock in/out
// records and the business-day bucket every sale lands in are all taken from
// the DEVICE. Half a trading day was being stamped onto tomorrow, silently, on
// a till that otherwise looked perfectly healthy — and nothing downstream can
// tell a mis-zoned row from a genuine one after the fact.
//
// So this is not pre-filled and forgotten: the installer refuses to run until
// somebody has looked at the zone and agreed with it. The auto-detected value
// is a starting point for that decision, never a substitute for it.

/** Offered when the browser cannot enumerate zones. US-first — that is the fleet. */
const FALLBACK_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Puerto_Rico', 'America/Toronto', 'America/Vancouver',
  'Europe/London', 'Europe/Dublin', 'Asia/Colombo', 'UTC',
];

/**
 * Every zone this console can offer.
 *
 * `Intl.supportedValuesOf` is the real list and is present in every browser
 * this console supports; the fallback exists so a missing Intl feature degrades
 * to a usable short list rather than an empty dropdown that cannot be
 * satisfied — and an empty dropdown, with the install gated on a choice, would
 * mean no till could be installed at all.
 */
export function listTimeZones(): string[] {
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    const zones = typeof supported === 'function' ? supported('timeZone') : [];
    if (Array.isArray(zones) && zones.length) return zones;
  } catch {
    /* fall through to the short list */
  }
  return FALLBACK_ZONES;
}

/**
 * The zone to open on: wherever this console is actually running.
 *
 * This is the operator's own machine, which on a bench install is the same
 * place as the till and therefore right. It is offered as a pre-selection the
 * operator must still confirm — see the header. Returns '' when the browser
 * reports a zone that is not in the offered list, so the panel shows an
 * unanswered required field rather than a value the installer would reject.
 */
export function detectTimeZone(zones: string[] = listTimeZones()): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && zones.includes(tz) ? tz : '';
  } catch {
    return '';
  }
}

/** What the clock on the till will read, so the operator can check it against the room. */
export function describeTimeZone(tz: string, now: Date = new Date()): string {
  if (!tz) return '';
  try {
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: '2-digit', weekday: 'short', month: 'short', day: 'numeric',
    }).format(now);
    const abbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(now).find((p) => p.type === 'timeZoneName')?.value || '';
    return abbr ? `${time} (${abbr})` : time;
  } catch {
    return '';
  }
}

/**
 * Accepted by both install doors and by this panel. Deliberately a SHAPE check
 * and not a list membership check: the agent runs on the operator's machine and
 * may know zones this browser's Intl build does not, and the device is the
 * final authority on whether a zone exists. What this must stop is a value
 * reaching argv that is not a zone at all.
 */
export function isValidTimeZone(tz: unknown): tz is string {
  return typeof tz === 'string' && /^[A-Za-z][A-Za-z0-9+_-]*(?:\/[A-Za-z0-9+_-]+){0,2}$/.test(tz) && tz.length <= 64;
}
